import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import MainLayout from '../layouts/MainLayout';
import MessagesTable from '../components/MessagesTable';
import MessagesPagination from '../components/ClientPagination';
import useApi from '../utils/useApi';
import { useLoading } from '../context/LoadingContext';
import { Modal } from 'bootstrap';
import ConfirmPasswordModal from '../components/ConfirmPasswordModal';
import EmailLayout from '../components/EmailLayout';

const BrowseMessages = () => {
  const { callApi } = useApi();
  const { setLoading } = useLoading();
  const [messages, setMessages] = useState([]);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLetter, setSearchLetter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const messagesPerPage = parseInt(process.env.REACT_APP_MESSAGES_PER_PAGE) || 25;
  const [totalMessages, setTotalMessages] = useState(0);
  const [messageDetail, setMessageDetail] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Date range filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [typingTimeout, setTypingTimeout] = useState(null);

  // Fetch messages
  const fetchMessages = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        length: messagesPerPage,
        keyword: debouncedSearchQuery || undefined,
        startsWith: searchLetter !== 'All' ? searchLetter : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        userEmail: userEmail || undefined,
      };

      const response = await callApi({
        endpoint: '/admin/email-messages/get-all-messages',
        params,
      });

      setMessages(response.data.messages || []);
      setTotalMessages(response.data.totalMessages || 0);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel
  const handleExportExcel = async () => {
    try {
      setLoading(true);
      
      const params = {
        keyword: debouncedSearchQuery || undefined,
        startsWith: searchLetter !== 'All' ? searchLetter : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        userEmail: userEmail || undefined,
      };

      const response = await callApi({
        endpoint: '/admin/email-messages/export-excel',
        params,
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      let filename = 'email-messages';
      if (startDate || endDate) {
        const start = startDate || 'start';
        const end = endDate || 'end';
        filename += `_${start}_to_${end}`;
      }
      if (userEmail) {
        filename += `_${userEmail.replace(/[@.]/g, '_')}`;
      }
      filename += '.xlsx';
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Export Successful',
        text: 'Email messages exported successfully!',
        timer: 2000,
      });
    } catch (error) {
      console.error('Error exporting messages:', error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: error.message || 'Failed to export email messages',
      });
    } finally {
      setLoading(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setUserEmail('');
    setSearchQuery('');
    setSearchLetter('All');
    setCurrentPage(1);
  };

  // Debounced search
  useEffect(() => {
    if (typingTimeout) clearTimeout(typingTimeout);
    setTypingTimeout(
      setTimeout(() => {
        setDebouncedSearchQuery(searchQuery); // Apply search query
      }, 500) // Wait 500ms after typing stops
    );
  }, [searchQuery]);

  // Fetch messages whenever the search query, currentPage, searchLetter, or filters change
  useEffect(() => {
    fetchMessages();
  }, [currentPage, debouncedSearchQuery, searchLetter, startDate, endDate, userEmail]);

  // View message details with header and footer
  const viewDetails = (message) => {
    setMessageDetail(message); // Display message content in modal
    const modalElement = document.getElementById('messageDetailsModal');
    const modalInstance = new Modal(modalElement);
    modalInstance.show();
  };

  return (
    <MainLayout>
      <div className="inner_full_right_part">
        <div className="row">
          <div className="col-md-12">
            <div className="global_cardbox">
              {/* Header with all filters in one line */}
              <div className="global_card_header">
                <div className="row align-items-center">
                  <div className="col-12">
                    <div className="d-flex align-items-center gap-2">
                      {/* Email Messages Title */}
                      <div className="global_head_tag" style={{ flex: '0 0 auto', fontSize: '18px', fontWeight: '600' }}>
                        <span><i className="fa-solid fa-envelope"></i></span> Email Messages<em></em>
                      </div>

                      {/* Search Input */}
                      <div style={{ flex: '0 0 250px' }}>
                        <input
                          type="search"
                          className="form-control"
                          placeholder="Search by Name or Email"
                          autoComplete="off"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          style={{ height: '38px' }}
                        />
                      </div>

                      {/* Clear Filters Button */}
                      <button
                        className="btn btn-secondary"
                        onClick={clearFilters}
                        title="Clear all filters"
                        style={{ height: '38px' }}
                      >
                        <i className="fa-solid fa-times"></i> Clear
                      </button>

                      {/* Start Date */}
                      <div style={{ flex: '0 0 160px' }}>
                        <input
                          type="date"
                          className="form-control"
                          placeholder="Start Date"
                          value={startDate}
                          onChange={(e) => {
                            setStartDate(e.target.value);
                            setCurrentPage(1);
                          }}
                          style={{ height: '38px' }}
                        />
                      </div>

                      {/* End Date */}
                      <div style={{ flex: '0 0 160px' }}>
                        <input
                          type="date"
                          className="form-control"
                          placeholder="End Date"
                          value={endDate}
                          onChange={(e) => {
                            setEndDate(e.target.value);
                            setCurrentPage(1);
                          }}
                          style={{ height: '38px' }}
                        />
                      </div>

                      {/* User Email Filter - Removed from here */}

                      {/* Export to Excel Button */}
                      <button
                        className="btn btn-success"
                        onClick={handleExportExcel}
                        disabled={totalMessages === 0}
                        title="Export to Excel"
                        style={{ height: '38px' }}
                      >
                        <i className="fa-solid fa-file-excel"></i> Export
                      </button>
                    </div>

                    {/* Filter Summary */}
                    {(startDate || endDate || userEmail || searchQuery) && (
                      <div className="mt-2">
                        <small className="text-muted">
                          <strong>Showing:</strong> {totalMessages} message(s)
                          {startDate && ` from ${new Date(startDate).toLocaleDateString()}`}
                          {endDate && ` to ${new Date(endDate).toLocaleDateString()}`}
                          {userEmail && ` • Email: ${userEmail}`}
                          {searchQuery && ` • Search: "${searchQuery}"`}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <MessagesTable
                messages={messages}
                selectedUsers={selectedMessages}
                toggleUserSelection={() => {}}
                toggleSelectAll={() => {}}
                viewDetails={viewDetails}
                deleteClient={() => {}}
              />
              <MessagesPagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalMessages / messagesPerPage)}
                setCurrentPage={setCurrentPage}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Message Details Modal with Email Layout */}
      <div className="modal fade" id="messageDetailsModal" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Message Details</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div className="modal-body">
              {/* Wrap message content in the EmailLayout */}
              <EmailLayout>
                <div
                  className="global_modal_body"
                  dangerouslySetInnerHTML={{ __html: messageDetail }}
                />
              </EmailLayout>
            </div>
          </div>
        </div>
      </div>

      <ConfirmPasswordModal
        show={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onSuccess={() => {}}
      />
    </MainLayout>
  );
};

export default BrowseMessages;
